/**
 * Geometry for the dialog quiz answer bubbles: each bubble's tail sits on the
 * edge that faces the actor and its tip leans towards the actor's head, like
 * the bubbles in the game.
 */

export type Point = { x: number; y: number };

export type Box = { left: number; right: number; top: number; bottom: number };

/** Where the tail sits along the bubble edge (from the top) and how far its tip shifts. */
export type TailGeometry = { y: number; dy: number };

/** Horizontal length of the tail, matching `.dialogquiz-bubble::after`. */
export const TAIL_LENGTH = 21;
/** Keeps the tail's base clear of the bubble's rounded corners. */
export const TAIL_EDGE_INSET = 24;
/** The tip never leans further than this, however close the head is. */
export const MAX_TAIL_DY = 26;

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

/**
 * @param actorOnLeft the actor stands left of the answers (the interaction's
 *   Right Side property), so the tail is on the bubble's left edge.
 */
export function computeTail(bubble: Box, head: Point, actorOnLeft: boolean): TailGeometry {
  const height = bubble.bottom - bubble.top;
  const tailY =
    height <= TAIL_EDGE_INSET * 2
      ? bubble.top + height / 2
      : clamp(head.y, bubble.top + TAIL_EDGE_INSET, bubble.bottom - TAIL_EDGE_INSET);
  const edgeX = actorOnLeft ? bubble.left : bubble.right;
  const dx = Math.max(Math.abs(head.x - edgeX), 1);
  const dy = clamp((TAIL_LENGTH * (head.y - tailY)) / dx, -MAX_TAIL_DY, MAX_TAIL_DY);
  return { y: Math.round(tailY - bubble.top), dy: Math.round(dy) };
}

/** The head's position inside an actor image, as fractions of its width and height. */
export type HeadAnchor = { x: number; y: number };

/** Composer outfit renders (640x800, standing, camera at chest height). */
export const COMPOSER_RENDER_HEAD: HeadAnchor = { x: 318 / 640, y: 140 / 800 };

export function headPoint(image: Box, anchor: HeadAnchor): Point {
  return {
    x: image.left + (image.right - image.left) * anchor.x,
    y: image.top + (image.bottom - image.top) * anchor.y,
  };
}
